import { Injectable } from "@angular/core";
import { KycStep, ProfileVerification, Step, StepSlug, StepStatus, TimeLineGroupStatus } from "./timeline.interface";
import { Router } from "@angular/router";
import { LoanDashboardService } from "../../loan-dashboard.service";
import { NetBankingService } from "../../../net-banking/net-banking.service";
import { StoreService } from "src/app/shared/services/store.service";
import { CryptoEncryptionService } from "src/app/shared/services/crypto-encryption.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { ToastrService } from "ngx-toastr";
import { map, Subscription } from "rxjs";

@Injectable({
    providedIn: "platform",
})
export class TimelineService {
    subscriptions: Subscription[] = [];
    steps: Step[] = [];

    constructor(
        private router: Router,
        private loanDashboardService: LoanDashboardService,
        private netBankingService: NetBankingService,
        private loaderService: LoaderService,
        private toastrService: ToastrService,
        private storeService: StoreService,
        private encrypt: CryptoEncryptionService
    ) {}

    async createSteps(stepsData: any[], isContinue = false): Promise<Step[]> {
        this.steps = [];

        stepsData.forEach((res) => {
            if (!res.display) return;

            let currentObj: Step = {
                stepId: res?.stepId || "",
                title: res?.title || "",
                stepIcon: res?.stepIcon || "",
                status: "current",
                statusCode: res?.statusCode || "",
                stepSlug: res?.stepSlug || "",
                statusMsg: res.stepStatusMessage || "",
                statusMsgIcon: "",
                msg: res.msg || "",
                other: res,
            };

            if (res.statusCode) {
                switch (res.statusCode) {
                    case StepStatus.NOT_INITIATED:
                        currentObj.status = "not-initiated";
                        currentObj.stepIcon = res?.disabledStepIcon || "";
                        currentObj.statusMsg = "Not Started";
                        currentObj.statusMsgIcon = "";
                        break;
                    case StepStatus.PENDING_FROM_USER_SIDE_ACTION:
                    case StepStatus.PENDING_FROM_USER_SIDE_2_ACTION:
                        if (res?.stepSlug == StepSlug.LOAN_DISBURSEMENT) {
                            currentObj.status = "in-verification";
                            currentObj.statusMsg = "IN PROGRESS";
                            currentObj.msg = "Your loan is being disbursed, it may take 15–20 minutes.";
                            currentObj.statusMsgIcon = "assets/svg/icons/zoom-in-area.svg";
                        } else {
                            currentObj.status = "pending";
                            currentObj.statusMsg = "Action pending";
                            currentObj.statusMsgIcon = "assets/svg/icons/spinner.svg";
                        }
                        break;
                    case StepStatus.PENDING_FROM_SERVER_SIDE:
                    case StepStatus.IN_ANALYSIS:
                        currentObj.status = "in-verification";
                        currentObj.statusMsg =
                            res?.stepSlug === StepSlug.LOAN_DISBURSEMENT ? "IN PROGRESS" : "Under Review";
                        currentObj.statusMsgIcon = "assets/svg/icons/zoom-in-area.svg";

                        currentObj.msg =
                            res?.stepSlug === StepSlug.LOAN_DISBURSEMENT
                                ? "Your loan is being disbursed, it may take 15–20 minutes."
                                : currentObj.msg;

                        break;
                    case StepStatus.FAILED:
                        currentObj.status = "declined";
                        currentObj.statusMsg = "Verification Failed";
                        currentObj.statusMsgIcon = "assets/svg/icons/info-error-circle-fill.svg";
                        currentObj.msg =
                            res?.stepSlug === StepSlug.LOAN_DISBURSEMENT
                                ? "The loan has been initiated from our side. We are currently facing a technical issue from your bank's side. It may take up to 2 working days."
                                : currentObj.msg || "Something needs your attention. Please try again.";

                        break;
                    case StepStatus.DECLINED:
                        currentObj.status = "declined";
                        currentObj.statusMsg = "Verification Failed";
                        currentObj.statusMsgIcon = "assets/svg/icons/info-error-circle-fill.svg";
                        currentObj.msg =
                            res?.stepSlug === StepSlug.LOAN_DISBURSEMENT
                                ? "The loan has been initiated from our side. We are currently facing a technical issue from your bank's side. It may take up to 2 working days."
                                : currentObj.msg || "Something needs your attention. Please try again.";
                        break;
                    case StepStatus.COMPLETED:
                    case StepStatus.SKIP:
                        currentObj.status = "completed";
                        currentObj.statusMsg = "Completed";
                        currentObj.statusMsgIcon = "assets/svg/icons/check.svg";
                        break;
                }

                this.steps.push(currentObj);
            }
        });

        if (isContinue) {
            await this.onContinue(this.steps);
        }

        return this.steps;
    }

    getStepDetail(data: any) {
        if (!data?.stepSlug || !data?.statusCode) return;

        // Inform Popup
        if (
            StepSlug.LOAN_DISBURSEMENT != data.stepSlug &&
            [StepStatus.PENDING_FROM_SERVER_SIDE, StepStatus.IN_ANALYSIS].includes(data.statusCode)
        ) {
            this.router.navigate(["/loan/timeline"]);

            return;
        } else if (
            StepSlug.LOAN_DISBURSEMENT == data.stepSlug &&
            [StepStatus.PENDING_FROM_SERVER_SIDE, StepStatus.PENDING_FROM_USER_SIDE_ACTION].includes(data.statusCode)
        ) {
            this.router.navigate(["/loan/disbursement/in-progress"]);
            return;
        }

        if (
            ![
                StepStatus.PENDING_FROM_USER_SIDE_ACTION,
                StepStatus.PENDING_FROM_USER_SIDE_2_ACTION,
                StepStatus.FAILED,
                StepStatus.DECLINED,
            ].includes(data.statusCode)
        ) {
            return;
        }

        const actionMapping: Record<string, (statusCode: string, payload: any) => void> = {
            [StepSlug.NB_BS_AG_ETC]: (statusCode, payload) => this.netBanking(statusCode, payload),
            [StepSlug.VIDEO_KYC]: (statusCode, payload) => this.video(statusCode, payload),
            [StepSlug.ESIGN_STEP]: (statusCode, payload) => this.ESign(statusCode, payload),
            [StepSlug.PN_VERIF_STEP]: (statusCode, payload) => this.panCard(statusCode, payload),
            [StepSlug.EMP_VERIFI_STEP]: (statusCode, payload) => this.employment(statusCode, payload),
            [StepSlug.EMP_VERIFI_STEP_SS]: (statusCode, payload) => this.salarySlip(statusCode, payload),
            [StepSlug.ADDRESS_VERIFICATION_STEP]: (statusCode, payload) => this.document(statusCode, payload),
            [StepSlug.FINAL_VERIFICATION]: (statusCode, payload) => this.fv(statusCode, payload),
            [StepSlug.CONTACT_VERFICATION_STEP]: (statusCode, payload) => this.contact(statusCode, payload),
            [StepSlug.BANK_ACC_VERIF_STEP]: (statusCode, payload) => this.pennyDrop(statusCode, payload),
            [StepSlug.LOAN_DISBURSEMENT]: (statusCode, payload) => this.disbursement(statusCode, payload),
            [StepSlug.ENACH_STEP]: (statusCode, payload) => this.eMandateUpdate(statusCode, payload),
            [StepSlug.USER_APPROVAL_STEP]: (statusCode, payload) => this.userApproval(statusCode, payload),
            [StepSlug.CKYC_VERIF_STEP]: (statusCode, payload) => this.ckyc(statusCode, payload),
            [StepSlug.AADHAR_KYC_STEP]: (statusCode, payload) => this.aadharKyc(statusCode, payload),
            [StepSlug.PROFILE_RETAKE_STEP]: (statusCode, payload) => this.profileRetake(statusCode, payload),
            [StepSlug.PROFILE_VERIFICATION_STEP]: (statusCode, payload) =>
                this.profileVerificationStep(statusCode, payload),
            [StepSlug.KYC_STEP]: (statusCode, payload) => this.kyc(statusCode, payload),
        };

        const action = actionMapping[data.stepSlug];

        if (action) {
            action(data.statusCode, data);
        }
    }

    employment(statusCode: string, payload: any): void {
        const draft: any = JSON.parse(this.storeService.get("employment") || "{}");
        const companyFromLocal = draft.company || draft;
        const companyFromPayload = payload?.other?.data?.approvedCmp || payload?.other?.data?.currentCompany || {};

        const isValidFromPayload =
            companyFromPayload?.companyId > 0 &&
            companyFromPayload?.companyName &&
            companyFromPayload?.companyType &&
            companyFromPayload?.searchType;

        if (isValidFromPayload) {
            const encryptData: any = this.encrypt.encryptionAES(JSON.stringify(payload));
            this.storeService.set({ key: "existingCompanyData", value: encryptData });
            this.router.navigate(["/loan/employment/exist-company"]);
            return;
        }

        if (companyFromLocal?.companyName) {
            this.router.navigate(["/loan/employment"]);
            return;
        } else {
            this.router.navigate(["/loan/employment/company-search"]);
        }
    }

    video(statusCode: string, payload: any): void {
        this.router.navigate(["loan/video-kyc"]);
    }

    ESign(statusCode: string, payload: any): void {
        this.router.navigate(["/loan/e-sign"]);
    }

    panCard(statusCode: string, payload: any): void {
        console.log("Method not implemented.", payload?.stepSlug);
    }

    salarySlip(statusCode: string, payload: any): void {
        console.log("Method not implemented.", payload?.stepSlug);
    }

    document(statusCode: string, payload: any): void {
        console.log("Method not implemented.", payload?.stepSlug);
    }

    fv(statusCode: string, payload: any): void {
        console.log("Method not implemented.", payload?.stepSlug);
    }

    contact(statusCode: string, payload: any): void {
        console.log("Method not implemented.", payload?.stepSlug);
    }

    pennyDrop(statusCode: string, payload: any): void {
        this.router.navigate(["loan/bank-verification"]);
    }

    disbursement(statusCode: string, payload: any): void {
        console.log("Method not implemented.", payload?.stepSlug);
    }

    eMandateUpdate(statusCode: string, payload: any): void {
        this.router.navigate(["loan/e-mandate"]);
    }

    userApproval(statusCode: string, payload: any): void {
        this.router.navigate(["/loan/user-approval"]);
    }

    ckyc(statusCode: string, payload: any): void {
        let mobileNumber: string = payload?.other?.data?.mobileNumber || "";
        let panNumber: string = payload?.other?.data?.panNumber || "";
        this.storeService.set({ key: "ckycDataMobile", value: mobileNumber });
        this.storeService.set({ key: "ckycDataPan", value: panNumber });
        this.router.navigate(["/loan/c-kyc"]);
    }

    netBanking(statusCode: string, payload: any): void {
        if (
            payload?.other?.data?.redirectScreenData &&
            Object.keys(payload?.other?.data?.redirectScreenData).length &&
            statusCode == StepStatus.FAILED
        ) {
            if (payload?.other?.data?.redirectScreenData?.isAARedirect) {
                this.router.navigate(["/loan/net-banking/failed-aa"], {
                    state: { redirectScreenData: payload?.other?.data?.redirectScreenData },
                });
            } else if (payload?.other?.data?.redirectScreenData?.isNetBankingRedirect) {
                this.router.navigate(["/loan/net-banking/failed-nb"], {
                    state: { redirectScreenData: payload?.other?.data?.redirectScreenData },
                });
            }
        } else {
            this.getBankDetails();
        }
    }

    // Get the last bank details
    getBankDetails() {
        this.loaderService.show();
        this.subscriptions.push(
            this.netBankingService
                .getBankDetails()
                .pipe(
                    map((res: any) => {
                        if (res.success && Object.keys(res.data).length > 0) {
                            this.router.navigate(["/loan/net-banking/exist-bank-detail"], { state: res?.data });
                        } else {
                            this.chooseFlow();
                        }
                        this.loaderService.hide();
                    })
                )
                .subscribe()
        );
    }

    // Verify the periodic consent
    periodicConsent() {
        this.loaderService.show();
        this.subscriptions.push(
            this.netBankingService
                .periodicConsent()
                .pipe(
                    map((res: any) => {
                        if (res.success == true) {
                            this.loaderService.hide();
                            this.loanDashboardService.getDashboardDetails();
                        } else {
                            this.loaderService.hide();
                            this.toastrService.error(res.message);
                        }
                    })
                )
                .subscribe()
        );
    }

    // Choose AA or NetBanking
    chooseFlow() {
        this.router.navigate(["/loan/net-banking/bank-account-verify"]);
    }

    aadharKyc(statusCode: string, payload: any) {
        this.router.navigate(["/loan/kyc/dg-locker"]);
    }

    profileRetake(statusCode: string, payload: any) {
        this.router.navigate(["/loan/profile-verification/capture-profile-pic"]);
    }

    profileVerificationStep(statusCode: string, payload: any) {
        const stage = payload?.other?.data?.stage ?? ProfileVerification?.NONE;
        if (stage == ProfileVerification?.LIVENESS) {
            this.router.navigate(["/loan/profile-verification/capture-profile-pic"]);
        } else if (stage == ProfileVerification?.EMAIL_VERIFICATION) {
            this.router.navigate(["/loan/profile-verification/email"]);
        } else if (stage == ProfileVerification?.PROFILE_DETAILS) {
            this.router.navigate(["/loan/profile-verification"]);
        } else {
            this.router.navigate(["/loan/timeline"]);
        }
    }

    kyc(statusCode: string, payload: any) {
        const stage = payload?.other?.data?.stage ?? KycStep?.NONE;
        if (stage == KycStep?.DIGI_LOCKER) {
            this.router.navigate(["/loan/kyc/dg-locker"]);
        } else if (stage == KycStep?.CURRENT_ADDRESS) {
            this.router.navigate(["/loan/kyc/change-current-address"]);
        } else if (stage == KycStep?.PAN) {
            this.router.navigate(["/loan/kyc/pan-kyc"]);
        } else {
            this.router.navigate(["/loan/timeline"]);
        }
    }

    async goToNextStep(res: any) {
        if (res?.stage === "TIMELINE") {
            const isRequiredStep = await this.requiredStep(res?.requiredSteps ?? {});
            if (isRequiredStep) return;

            const timeLineArr = res?.timeLine ?? [];
            const selectedTimeline = timeLineArr.find(
                (item: any) => item.groupStatus === TimeLineGroupStatus.IN_PROGRESS
            );

            if (!selectedTimeline?.steps) return;

            const timeline = selectedTimeline.steps.filter((s: any) => s.display);
            await this.createSteps(timeline, true);
        } else {
            // this.router.navigate(["/loan/timeline"]);
            this.loanDashboardService.getDashboardDetails(true);
        }
    }

    async onContinue(steps: Step[]): Promise<void> {
        const actionableStatuses: any = [
            StepStatus.PENDING_FROM_USER_SIDE_ACTION,
            StepStatus.PENDING_FROM_USER_SIDE_2_ACTION,
            StepStatus.FAILED,
            StepStatus.DECLINED,
        ];

        const nextStep = steps.find((step) => actionableStatuses.includes(step.statusCode));

        if (nextStep && nextStep.stepSlug !== StepSlug.FINAL_VERIFICATION) {
            this.getStepDetail(nextStep);
            return;
        }

        const isPendingServerSide: any = steps.find((step) =>
            [StepStatus.PENDING_FROM_SERVER_SIDE, StepStatus.IN_ANALYSIS].includes(step.statusCode as any)
        );

        if (isPendingServerSide?.stepSlug != "" && isPendingServerSide.stepSlug == StepSlug.FINAL_VERIFICATION) {
            this.router.navigate(["/loan/info"], {
                state: {
                    icon: "assets/svg/icons/clock-progress-gb.svg",
                    title: "Your loan request is getting reviewed",
                    description: "Sit back and relax, this won’t take long.",
                    rightButtonName: "Notify Me When Ready",
                    rightButtonRoute: "/loan/timeline",
                },
            });
            return;
        }

        if (isPendingServerSide?.stepSlug != "" && isPendingServerSide.stepSlug !== StepSlug.LOAN_DISBURSEMENT) {
            this.router.navigate(["/loan/timeline"]);

            return;
        } else if (
            StepSlug.LOAN_DISBURSEMENT == isPendingServerSide.stepSlug &&
            [StepStatus.PENDING_FROM_SERVER_SIDE, StepStatus.PENDING_FROM_USER_SIDE_ACTION].includes(
                isPendingServerSide.statusCode
            )
        ) {
            this.router.navigate(["/loan/disbursement/in-progress"]);
            return;
        }

        this.router.navigate(["/loan/timeline"]);
    }

    async requiredStep(requiredSteps: Record<string, any> = {}): Promise<boolean> {
        if (!requiredSteps || Object.keys(requiredSteps).length === 0) {
            return false;
        }

        if (requiredSteps["COMMON_SURVEY"]) {
            await this.router.navigateByUrl("/", { skipLocationChange: true });
            await this.router.navigate(["/loan/purpose-of-loan"]);
            return true;
        }

        if (requiredSteps["SOFT_LOAN_SURVEY"]) {
            await this.router.navigateByUrl("/", { skipLocationChange: true });
            await this.router.navigate(["/loan/soft-approval"]);
            return true;
        }

        return false;
    }
}
